import { ReactNode } from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  weeklyCrestCap,
  weeklyCrestEarned,
} from 'app/frontend/base/helpers/crestCap'
import type { UncappedCrestSource } from 'app/frontend/types/custom/Season'
import { cn } from '@/lib/utils'
import ItemIcon from 'app/frontend/base/components/ItemIcon'

type Props = {
  crest: {
    first_period: number
    weekly_increase: number
    uncapped_crests?: Record<string, UncappedCrestSource>
  }
  period: number
  earned: number
  earnedSources?: Record<string, number>
  trackName?: string
  className?: string
  children: ReactNode
}

const CrestCapTooltip = ({
  crest,
  period,
  earned,
  earnedSources = {},
  trackName,
  className,
  children,
}: Props) => {
  const weeklyCap = weeklyCrestCap(crest, period)
  const weeklyEarned = weeklyCrestEarned(earned, weeklyCap, earnedSources)
  const sources = Object.entries(crest.uncapped_crests ?? {})

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className={className}>{children}</div>
      </TooltipTrigger>
      <TooltipContent className='min-w-48 px-3 py-2.5'>
        <div className='text-center'>
          {trackName ? (
            <div className='text-xs font-semibold'>{trackName}</div>
          ) : null}
          <div className='text-base font-semibold tabular-nums'>
            <span
              className={
                weeklyEarned === weeklyCap
                  ? 'text-success-foreground'
                  : 'text-warning-foreground'
              }
            >
              {weeklyEarned}
            </span>
            {' / '}
            {weeklyCap}
          </div>
        </div>

        {sources.length > 0 ? (
          <div className='mt-2 border-t pt-2'>
            <div className='mb-1.5 text-[11px] text-muted-foreground'>
              Uncapped sources
            </div>
            <ul className='space-y-1'>
              {sources.map(([source, { amount, name, icon }]) => {
                const complete = !!earnedSources[source]
                const sourceEarned = complete ? amount : 0

                return (
                  <li key={source}>
                    <a
                      href={`https://wowhead.com${source}`}
                      target='_blank'
                      rel='noreferrer'
                      className='flex items-center gap-1.5'
                    >
                      {icon ? (
                        <ItemIcon name={icon} className='h-4 w-4' />
                      ) : null}
                      <span className='flex-1'>{name}</span>
                      <span
                        className={cn(
                          'tabular-nums',
                          complete
                            ? 'text-success-foreground'
                            : 'text-warning-foreground',
                        )}
                      >
                        {sourceEarned} / {amount}
                      </span>
                    </a>
                  </li>
                )
              })}
            </ul>
          </div>
        ) : null}
      </TooltipContent>
    </Tooltip>
  )
}

export default CrestCapTooltip
